import { Field } from '@cloudflare/kumo';
import { useMemo, useState } from 'react';
import { Badge } from '@cloudflare/kumo';
import { ICONS } from '../ui/icons';

export interface TagCount { tag: string; count: number }

/** Opção do Multiselect com `value` garantido (o `OptionDefinition` do Cloudscape deixa opcional). */
interface TagOption { value: string; label: string; description?: string }

const CREATE = '\u0000criar:';

/**
 * Seleção de tags em chips (como o seletor de clusters do Atlas): busca,
 * "×" em cada chip e limpar tudo. Com `allowCreate`, o texto digitado vira
 * uma tag nova ("Criar tag …").
 *
 * O `Combobox` do LeafyGreen não existe no Cloudscape. A troca foi feita pelo
 * `Multiselect`, que entrega múltipla seleção com busca embutida:
 *  - sem `allowCreate`, o filtro é automático (`filteringType="auto"`);
 *  - com `allowCreate`, o filtro é manual (`filteringType="manual"`): assim o
 *    componente avisa o texto digitado por `onLoadItems` e a lista de opções
 *    pode incluir a entrada "Criar tag …" com o valor prefixado por `CREATE`.
 */
export function TagCombobox({ label, ariaLabel, placeholder, value, onChange, options, allowCreate = false, closeOnSelect = false }: {
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  value: string[];
  onChange: (tags: string[]) => void;
  options: TagCount[];
  allowCreate?: boolean;
  /** Em modal: fecha o menu a cada escolha */
  closeOnSelect?: boolean;
  /** Mantido por compatibilidade de API: o `Multiselect` do Cloudscape não tem
   *  prop de tamanho. */
  size?: 'default' | 'small' | 'xsmall' | 'large';
}) {
  const [filterText, setFilterText] = useState('');
  const typed = filterText.trim().slice(0, 50);
  const all = useMemo(() => {
    const known = new Map(options.map((o) => [o.tag, o.count]));
    for (const v of value) if (!known.has(v)) known.set(v, 0);
    return [...known.entries()].map(([tag, count]) => ({ tag, count }));
  }, [options, value]);
  const canCreate = allowCreate && typed && !all.some((o) => o.tag.toLowerCase() === typed.toLowerCase());

  const list = useMemo<TagOption[]>(() => {
    const known: TagOption[] = all.map((o) => ({
      value: o.tag,
      label: o.tag,
      description: o.count ? `${o.count.toLocaleString('pt-BR')} contato(s)` : undefined,
    }));
    if (!allowCreate) return known;
    // Filtro manual: só entram as tags que casam com o texto digitado —
    // mas as já selecionadas continuam na lista para poderem ser removidas.
    const filtered = typed
      ? known.filter((o) => o.value.toLowerCase().includes(typed.toLowerCase()))
      : known;
    const missing = value.filter((v) => !filtered.some((o) => o.value === v));
    const withSelected: TagOption[] = [...filtered, ...missing.map((v) => ({ value: v, label: v }))];
    if (!canCreate) return withSelected;
    return [{ value: CREATE + typed, label: `Criar tag "${typed}"` }, ...withSelected];
  }, [all, allowCreate, typed, canCreate, value]);

  const [open, setOpen] = useState(false);
  const toggle = (raw: string) => {
    const tag = raw.startsWith(CREATE) ? raw.slice(CREATE.length) : raw;
    onChange(value.includes(tag) ? value.filter((v) => v !== tag) : [...new Set([...value, tag])]);
    setFilterText('');
    if (closeOnSelect) setOpen(false);
  };
  const shown = allowCreate ? list : list.filter((o) => !typed || o.value.toLowerCase().includes(typed.toLowerCase()));

  const control = (
    <div className="relative">
      <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-lg border border-kumo-line bg-kumo-base px-2 py-1">
        {value.map((v) => (
          <Badge key={v} variant="neutral">
            {v}
            <button type="button" className="ml-1" aria-label={`Remover a tag ${v}`} onClick={() => toggle(v)}><ICONS.X size={12} /></button>
          </Badge>
        ))}
        <input
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          className="min-w-24 flex-1 bg-transparent text-sm outline-none"
          aria-label={ariaLabel ?? label ?? 'Tags'}
          placeholder={value.length ? undefined : placeholder}
          value={filterText}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setFilterText(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && shown[0]) { e.preventDefault(); toggle(shown[0].value); } }}
        />
      </div>
      {open && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-kumo-line bg-kumo-base p-1 shadow-lg" role="listbox" aria-multiselectable>
          {shown.length === 0 && <li className="px-2 py-1.5 text-sm text-kumo-subtle">{allowCreate ? 'Digite para criar uma tag' : 'Nenhuma tag encontrada'}</li>}
          {shown.map((o) => (
            <li key={o.value} role="option" aria-selected={value.includes(o.value)}>
              <button type="button" className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-kumo-tint" onClick={() => toggle(o.value)}>
                <span>{value.includes(o.value) ? '✓ ' : ''}{o.label}</span>
                {o.description && <span className="text-xs text-kumo-subtle">{o.description}</span>}
              </button>
            </li>
          ))}
          <li><button type="button" className="w-full rounded px-2 py-1 text-left text-xs text-kumo-subtle" onClick={() => setOpen(false)}>Fechar</button></li>
        </ul>
      )}
    </div>
  );

  return label ? <Field label={label}>{control}</Field> : control;
}
